import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../lib/firebase";
import { Button } from "../components/ui/button";
import Seo from "../components/Seo";
import { PageHeader, SectionHeading } from "../components/sections/Shared";
import {
  COMPLIANCE_TITLE,
  COMPLIANCE_INTRO,
  COMPLIANCE_FINAL_STATEMENT,
  VERIFIED_STANDARDS,
  MEDICAL_CLARIFICATIONS,
  HIPAA_STATEMENT,
  REGISTRATION_FIELDS
} from "../data/compliance";

export default function Compliance() {
  const [registrations, setRegistrations] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "site_compliance", "registrations"),
      (snap) => setRegistrations(snap.exists() ? snap.data() : {}),
      (err) => console.warn("registrations read error:", err?.message)
    );
    return () => unsub();
  }, []);

  // Only show registrations an administrator has entered AND activated.
  const activeRegs = REGISTRATION_FIELDS
    .map((f) => ({ ...f, ...(registrations?.[f.key] || {}) }))
    .filter((r) => r.active && r.value);

  return (
    <>
      <Seo
        title="Compliance, Safety & Service Standards | 518 Courier"
        description="518 Courier (OBLED Corporation d/b/a 518 Courier) operates with documented chain-of-custody, proof-of-delivery, HIPAA-aligned privacy and safety procedures for medical and business deliveries across New York's Capital Region. Call 518-954-4701."
        path="/compliance"
      />
      <PageHeader
        eyebrow="Compliance, Safety & Service Standards"
        title="Documented, secure,"
        accent="and ready to deliver."
        description="OBLED Corporation d/b/a 518 Courier — operational standards for secure, time-sensitive, medical and business deliveries across the Capital Region."
        image="https://images.pexels.com/photos/6169129/pexels-photo-6169129.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />

      <section className="py-14 md:py-20 bg-white">
        <div className="container-518 max-w-4xl">
          <div className="flex items-start gap-4 p-6 rounded-2xl bg-slate-50 border border-slate-200" data-testid="compliance-intro">
            <Icons.ShieldCheck className="w-6 h-6 text-[#f59321] mt-0.5 shrink-0" />
            <p className="text-slate-600 leading-relaxed text-lg">{COMPLIANCE_INTRO}</p>
          </div>
        </div>
      </section>

      {/* Verified operational standards */}
      <section className="pb-8 md:pb-14 bg-white">
        <div className="container-518">
          <SectionHeading eyebrow="Operational standards" title="What we maintain," accent="every route." align="left" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="compliance-standards">
            {VERIFIED_STANDARDS.map((s) => {
              const Icon = Icons[s.icon] || Icons.CheckCircle2;
              return (
                <div key={s.title} className="card-lift bg-white rounded-2xl border border-slate-200 p-6 flex flex-col">
                  <div className="w-12 h-12 rounded-xl bg-[#0f2847] text-white flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-display font-bold text-[#0f2847] text-lg mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Government registrations — only shown once admin activates verified values */}
      {activeRegs.length > 0 && (
        <section className="py-12 bg-slate-50 border-y border-slate-200">
          <div className="container-518 max-w-4xl">
            <SectionHeading eyebrow="Registrations" title="Verified" accent="registrations." align="left" />
            <div className="grid sm:grid-cols-2 gap-4" data-testid="compliance-registrations">
              {activeRegs.map((r) => (
                <div key={r.key} className="bg-white rounded-2xl border border-slate-200 p-5">
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">{r.label}</div>
                  <div className="font-display text-xl font-extrabold text-[#0f2847]">{r.value}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Medical & HIPAA */}
      <section className="py-16 md:py-20 bg-slate-50 border-t border-slate-200">
        <div className="container-518 max-w-4xl">
          <SectionHeading eyebrow="Medical courier" title="Handled by trained," accent="authorized personnel." align="left" />
          <div className="p-6 rounded-2xl bg-[#0f2847] text-white mb-6 flex items-start gap-3">
            <Icons.HeartPulse className="w-6 h-6 text-[#f59321] mt-0.5 shrink-0" />
            <p className="leading-relaxed text-slate-100">{HIPAA_STATEMENT}</p>
          </div>
          <ul className="space-y-3">
            {MEDICAL_CLARIFICATIONS.map((c, i) => (
              <li key={i} className="flex items-start gap-3 bg-white rounded-xl border border-slate-200 p-4">
                <Icons.CheckCircle2 className="w-5 h-5 text-[#f59321] mt-0.5 shrink-0" />
                <span className="text-sm text-slate-700 leading-relaxed">{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Documentation availability + final statement */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container-518 grid lg:grid-cols-2 gap-6 items-stretch">
          <div className="rounded-2xl border border-slate-200 p-8 bg-slate-50 flex flex-col">
            <Icons.FolderLock className="w-8 h-8 text-[#f59321] mb-4" />
            <h3 className="font-display text-2xl font-extrabold text-[#0f2847] mb-2">Compliance Document Center</h3>
            <p className="text-slate-600 leading-relaxed flex-1" data-testid="compliance-final-statement">{COMPLIANCE_FINAL_STATEMENT}</p>
            <Link to="/compliance-center" className="mt-6">
              <Button className="bg-[#0f2847] hover:bg-[#1e3a5f] text-white rounded-full h-12 px-6 font-semibold">Access document center <Icons.ArrowRight className="w-4 h-4 ml-2" /></Button>
            </Link>
          </div>
          <div className="rounded-2xl border border-slate-200 p-8 bg-[#0f2847] text-white stripe-bg flex flex-col">
            <Icons.FileText className="w-8 h-8 text-[#f59321] mb-4" />
            <h3 className="font-display text-2xl font-extrabold mb-2">Onboarding a medical or government account?</h3>
            <p className="text-slate-200 leading-relaxed flex-1">Capability statements, certificates of insurance, Business Associate Agreements and SOP documentation can be provided to qualified partners during onboarding or contract review.</p>
            <a href="tel:5189544701" className="mt-6">
              <Button variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-[#0f2847] rounded-full h-12 px-6 font-semibold"><Icons.PhoneCall className="w-4 h-4 mr-2" /> Call 518-954-4701</Button>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
